import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { AlertCircle, Clock } from 'lucide-react';
import { WellnessAssessmentType } from '../../utils/types/wellness_types';
import { DASS21_QUESTIONS, SCALE_LABELS as DASS21_SCALE } from './Constants/DASS21_QUESTIONS';
import { GAD7_QUESTIONS, SCALE_LABELS as GAD7_SCALE } from './Constants/GAD7_QUESTIONS';
import { PHQ9_QUESTIONS, SCALE_LABELS as PHQ9_SCALE } from './Constants/PHQ9_QUESTIONS';
import { AssessmentShell } from './components/AssessmentShell';

// ─── Dummy API helpers (replace with real fetch when APIs are ready) ──────────

interface TokenValidation {
  valid: boolean;
  expired?: boolean;
  deadlineDate?: string;
  assessmentType?: WellnessAssessmentType;
  companyName?: string;
}

async function validateToken(token: string): Promise<TokenValidation> {
  // TODO: replace with real call: GET /wellness/assess/validate?token=
  void token;
  await new Promise((r) => setTimeout(r, 600)); // simulate network
  return {
    valid: true,
    assessmentType: 'DASS21',
    companyName: 'HiAcuity',
    deadlineDate: '2026-07-15',
  };
}

interface Answer { questionId: string; score: number }

async function submitAssessment(token: string, answers: Answer[]): Promise<void> {
  // TODO: replace with real call: POST /wellness/assess/submit
  void token; void answers;
  await new Promise((r) => setTimeout(r, 800)); // simulate network
}

// ─── Per-type question config ─────────────────────────────────────────────────

const ASSESSMENT_CONFIG: Record<WellnessAssessmentType, { questions: typeof DASS21_QUESTIONS; scaleLabels: string[] }> = {
  DASS21: { questions: DASS21_QUESTIONS, scaleLabels: DASS21_SCALE },
  GAD7:   { questions: GAD7_QUESTIONS,   scaleLabels: GAD7_SCALE },
  PHQ9:   { questions: PHQ9_QUESTIONS,   scaleLabels: PHQ9_SCALE },
};

// ─── Main page ────────────────────────────────────────────────────────────────

type Stage = 'loading' | 'invalid' | 'expired' | 'ready';

export const WellnessAssessmentPage: React.FC = () => {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token') ?? '';

  const [stage, setStage] = useState<Stage>('loading');
  const [tokenData, setTokenData] = useState<TokenValidation | null>(null);

  useEffect(() => {
    if (!token) { setStage('invalid'); return; }
    validateToken(token).then((data) => {
      setTokenData(data);
      if (!data.valid) setStage('invalid');
      else if (data.expired) setStage('expired');
      else setStage('ready');
    }).catch(() => setStage('invalid'));
  }, [token]);

  if (stage === 'loading') {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(248, 250, 252, 0.8)' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={36} color='primary' />
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Loading your assessment…
          </Typography>
        </Box>
      </Box>
    );
  }

  if (stage === 'invalid') {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(248, 250, 252, 0.8)', px: 2 }}>
        <Paper
          elevation={0}
          variant='outlined'
          sx={{
            maxWidth: 440,
            width: '100%',
            textAlign: 'center',
            p: { xs: 4, sm: 5 },
            borderRadius: '24px',
            bgcolor: '#ffffff',
            boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Box
            sx={{
              mb: 2.5,
              display: 'inline-flex',
              height: 56,
              width: 56,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              bgcolor: 'rgba(239, 68, 68, 0.1)',
              color: 'error.main',
            }}
          >
            <AlertCircle size={32} />
          </Box>
          <Typography variant='h6' sx={{ fontWeight: 800, color: 'text.primary', mb: 1 }}>
            Invalid Link
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            This wellness assessment link is invalid or has already been used. Please contact the recruiter if you believe this is an error.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (stage === 'expired') {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(248, 250, 252, 0.8)', px: 2 }}>
        <Paper
          elevation={0}
          variant='outlined'
          sx={{
            maxWidth: 440,
            width: '100%',
            textAlign: 'center',
            p: { xs: 4, sm: 5 },
            borderRadius: '24px',
            bgcolor: '#ffffff',
            boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Box
            sx={{
              mb: 2.5,
              display: 'inline-flex',
              height: 56,
              width: 56,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              bgcolor: 'rgba(245, 158, 11, 0.1)',
              color: 'warning.main',
            }}
          >
            <Clock size={32} />
          </Box>
          <Typography variant='h6' sx={{ fontWeight: 800, color: 'text.primary', mb: 1 }}>
            Link Expired
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            This assessment link expired on{' '}
            <Typography component='span' sx={{ fontWeight: 700 }}>
              {tokenData?.deadlineDate
                ? new Date(tokenData.deadlineDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                : 'the deadline'}
            </Typography>
            . Please contact the recruiter to request a new invitation.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const assessmentType = tokenData?.assessmentType ?? 'DASS21';
  const config = ASSESSMENT_CONFIG[assessmentType];

  return (
    <AssessmentShell
      assessmentType={assessmentType}
      questions={config.questions}
      scaleLabels={config.scaleLabels}
      companyName={tokenData?.companyName}
      onSubmit={(answers) => submitAssessment(token, answers)}
    />
  );
};

